/* eslint-disable no-console */
import moment from 'moment';
import {
  IMultiSelectOption,
  IOption,
  IReactSelectOption,
} from '../models/ICommon';
import { formatMessage } from '../translations/format-message';
import userTypeEnum from './enums/user-type-enum';
import { UserTypeOptions, ViewTypeOptions } from './options.constant';
import viewTypeEnum from './enums/view-type-enum';

export const MAX_FILE_LIMIT = 10;
export const TEXTAREA_ROW_LENGTH = 3;
export const emptyValue = '-';
export const defautValueToBeChanged = -999;
export const superAdminUserName = 'admin@test.com';
export const minDate='0001-01-01T00:00:00.0000000Z'

// BorrowerType values accepted by the AddToolLoanDetails API.
export const BorrowerType = {
  Factory: 'FACTORY',
  Subcontractor: 'SUBCONTRACTOR',
};

export const getDaysOptions = (noOfDays: number) => {
  let optionsArray = [];
  for (let i = 1; i <= noOfDays; i++) {
    optionsArray.push({ key: i, value: i });
  }
  return optionsArray;
};

export const clearLocalStorageFields = () => {
  const lang: string | null = localStorage.getItem('oms_language');
  const pageLimitConfig: string | null = localStorage.getItem(
    'oms_pageLimitConfig'
  );
  const tagPageLimitConfig: string | null = localStorage.getItem(
    'oms_tagPageLimitConfig'
  );
  const selectedDays: string | null = localStorage.getItem('oms_selectedDays');
  localStorage.clear();
  localStorage.setItem('oms_language', lang === 'en' ? 'en' : 'jp');
  if (pageLimitConfig)
    localStorage.setItem('oms_pageLimitConfig', pageLimitConfig);
  if (tagPageLimitConfig)
    localStorage.setItem('oms_tagPageLimitConfig', tagPageLimitConfig);
  if (selectedDays) localStorage.setItem('oms_selectedDays', selectedDays);
};

// Function to combine date and time into a single Date object
const combineDateTime = (date: string, time: string): moment.Moment => {
  return moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss');
};

export const isDateTimeOverlap = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  existingData: any[],
  ssId: number
): boolean => {
  const newStart = combineDateTime(startDate, startTime);
  const newEnd = combineDateTime(endDate, endTime);

  for (const data of existingData) {
    const existingStart = combineDateTime(data.StartDate, data.StartTime);
    const existingEnd = combineDateTime(data.EndDate, data.EndTime);

    // Check if there is an overlap
    if (
      newStart.isBefore(existingEnd) &&
      newEnd.isAfter(existingStart) &&
      ssId != data.SsId
    ) {
      return true; // Overlap found
    }
  }

  return false; // No overlap
};

export const getComponentName = (name: string) => {
  return name.replace(/^\//, '');
};

export const SUPPORTED_FORMATS = [
  'text/csv',
  '.csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const SUPPORTED_FORMATS_ALL = [
  'text/csv',
  '.csv',
  '.xls',
  '.xlsx',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const SUPPORTED_FORMATS_DOCUMENTS = ['.pdf', 'application/pdf'];
export const SUPPORTED_FORMATS_IMAGES = [
  '.jpg',
  '.jpeg',
  '.png',
  'image/png',
  'image/jpeg',
  'image/jpg',
];

export const SUPPORTED_FORMATS_IMAGES_AND_PDF = [
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
];

export const fixTimezoneOffset = (date: string): string => {
  return new Date(new Date(date).toISOString())
    .toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '-');
};

export const fixTimezoneOffsetYYYYMMDD = (date: string): string => {
  return new Date(new Date(date).toISOString()).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // .replace(/\//g, '-');
};

export const convertUTCDateToLocalTimeZone = (date: string): string => {
  const utcDate = new Date(date + ' UTC');
  return utcDate.toLocaleString();
};

export const convertNonUTCDateToLocalTimeZone = (date: string): string => {
  const utcDate = new Date(date + 'T00:00:00Z');
  return utcDate.toLocaleDateString();
};

export const OrderViewColumnFields: IMultiSelectOption[] = [
  {
    label: 'EndUser Name',
    value: 'EndUserName',
  },
  {
    label: 'EndUser Order Id',
    value: 'EndUserOrderId',
  },
  {
    label: 'Kishu',
    value: 'Kishu',
  },
  {
    label: 'Order Id',
    value: 'OrderId',
  },
  {
    label: 'Tray Id',
    value: 'TrayId',
  },
  {
    label: 'Product Name',
    value: 'ProductId',
  },
  {
    label: 'Katashiki 1',
    value: 'Katashiki1',
  },
  {
    label: 'Katashiki 2',
    value: 'Katashiki2',
  },
  {
    label: 'Quantity',
    value: 'Quantity',
  },
  {
    label: 'Process Name',
    value: 'ProcessName',
  },
  {
    label: 'Product Comment 1',
    value: 'ProductComment1',
  },
  {
    label: 'Product Comment 2',
    value: 'ProductComment2',
  },
  {
    label: 'Planed Due Date',
    value: 'PlanedDueDate',
  },

  {
    label: 'Product Comment 3',
    value: 'ProductComment3',
  },
  {
    label: 'Product Comment 4',
    value: 'ProductComment4',
  },
  {
    label: 'Product Comment 5',
    value: 'ProductComment5',
  },
  {
    label: 'Product Comment 6',
    value: 'ProductComment6',
  },
];

export const getUserTypeOptions = (userTypeStr: string): IOption[] => {
  const userType = userTypeEnum[userTypeStr as keyof typeof userTypeEnum];

  if (userType === undefined) {
    return [{ id: -1, value: 'please_select', disabled: false }];
  }

  return [
    { id: -1, value: 'please_select', disabled: false },
    ...UserTypeOptions.filter((option) => option.id >= userType),
  ];
};

export const colorOptions: IOption[] = [
  {
    id: -1,
    value: 'please_select',
    disabled: false,
  },
  {
    id: 0,
    value: 'Red',
  },
  {
    id: 1,
    value: 'Green',
  },
  {
    id: 2,
    value: 'Yellow',
  },
  {
    id: 3,
    value: 'Blue',
  },
  {
    id: 4,
    value: 'Magenta',
  },
  {
    id: 5,
    value: 'Cyan',
  },
  {
    id: 6,
    value: 'White',
  },
];

export const timerOptions: IOption[] = [
  {
    id: -1,
    value: 'please_select',
    disabled: false,
  },
  {
    id: 10,
    value: '10s',
  },
  {
    id: 30,
    value: '30s',
  },
  {
    id: 60,
    value: '1m',
  },
  {
    id: 120,
    value: '2m',
  },
];

export const WeekendHighlightColor = 'red';

export const colorPalette = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
  '#393b79',
  '#637939',
  '#8c6d31',
  '#843c39',
  '#7b4173',
  '#3182bd',
  '#6baed6',
  '#9ecae1',
  '#c6dbef',
  '#e6550d',
  '#fd8d3c',
  '#fdae6b',
  '#fdd0a2',
  '#31a354',
  '#74c476',
  '#a1d99b',
  '#c7e9c0',
  '#756bb1',
  '#9e9ac8',
  '#bcbddc',
  '#dadaeb',
  '#636363',
  '#969696',
  '#bdbdbd',
  '#d9d9d9',
  '#393b79',
  '#5254a3',
  '#6b6ecf',
  '#9c9ede',
  '#637939',
  '#8ca252',
  '#b5cf6b',
  '#cedb9c',
  '#8c6d31',
  '#bd9e39',
  '#e7ba52',
  '#e7969c',
  '#d6616b',
  '#fcbba1',
  '#c49c94',
];

export const missingPartsOptions1: IReactSelectOption[] = [
  // {
  //   label: 'please_select',
  //   value: '-1',
  //   isDisabled: false,
  // },
  {
    label: '有',
    value: '有',
    isDisabled: false,
  },
  {
    label: '無',
    value: '無',
    isDisabled: false,
  },
];

export const missingPartsOptions2: IReactSelectOption[] = [
  {
    label: formatMessage('please_select'),
    value: '-1',
    isDisabled: false,
  },
  {
    label: '有',
    value: '有',
    isDisabled: false,
  },
  {
    label: '無',
    value: '無',
    isDisabled: false,
  },
];

export const SHORT_DAY_NAME_FORMAT = 'ddd';

export const OrderTableViewInitialSelectedColumns = [
  {
    label: 'EndUser Name',
    value: 'EndUserName',
  },
  {
    label: 'EndUser Order Id',
    value: 'EndUserOrderId',
  },
  {
    label: 'Order Id',
    value: 'OrderId',
  },
  {
    label: 'Kishu',
    value: 'Kishu',
  },
  {
    label: 'Tray Id',
    value: 'TrayId',
  },
  {
    label: 'Product Id',
    value: 'ProductName',
  },
  {
    label: 'Katashiki 1',
    value: 'Katashiki1',
  },
  {
    label: 'Quantity',
    value: 'Quantity',
  },
  {
    label: 'Next Process',
    value: 'NextProcess',
  },
];

export const fixTimezoneOffsetYYYYMMDDWithTimeStamp = (
  date: string
): string => {
  return moment.utc(date).utcOffset(540).format('YYYY/MM/DD HH:mm:ss');
};

// export const fixTimezoneOffsetYYYYMMDDWithTimeStamp1 = (
//   date: string
// ): string => {
//   return moment(date)
//     .tz('Asia/Tokyo')
//     .format('YYYY/MM/DD HH:mm:ss');
// };

// export const fixTimezoneOffsetYYYYMMDD = (date: string): string => {
//   return new Date(new Date(date).toISOString()).toLocaleDateString('ja-JP', {
//     year: 'numeric',
//     month: '2-digit',
//     day: '2-digit',
//   });
//   // .replace(/\//g, '-');
// };

export const OrderTableDefaultAllowedColumns = [
  'checkbox',
  'EndUserName',
  'EndUserOrderId',
  'OrderId',
  'Kishu',
  'TrayId',
  'ProductName',
  'Katashiki1',
  'Quantity',
];

export const getViewTypeOptions = (viewTypeStr: string): IOption[] => {
  const viewType = viewTypeEnum[viewTypeStr as keyof typeof viewTypeEnum];

  if (viewType === undefined) {
    return [{ id: -1, value: formatMessage('please_select'), disabled: false }];
  }

  return [
    { id: -1, value: formatMessage('please_select'), disabled: false },
    ...ViewTypeOptions.filter((option) => option.id >= viewType),
  ];
};
